import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, ArrowRight } from 'lucide-react';

const BRAND_NAME = "DH Design";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_3ded9da4-7891-48ae-9b29-bf144d630c6e/artifacts/76ag0ln1_1000021182.png";

// Friendly copy for the ?google_error=... query param returned by the
// native Google OAuth callback when something goes wrong before we can
// land the user inside the app.
const GOOGLE_ERROR_COPY = {
  missing_params: 'Google returned without a sign-in code. Please try again.',
  invalid_state: 'This sign-in link has expired. Please try again.',
  state_expired: 'This sign-in link has expired. Please try again.',
  token_exchange_failed: 'Google rejected the sign-in. Try again or use email/password.',
  token_exchange_exception: 'Could not reach Google. Try again in a moment.',
  missing_id_token: 'Google did not return a valid identity. Try again.',
  id_token_invalid: 'Google identity could not be verified. Try again.',
  email_unverified: 'Your Google account email is not verified.',
  account_rejected: 'Your account access has been rejected. Contact support.',
  not_configured: 'Google sign-in is temporarily unavailable.',
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // Surface OAuth callback errors / pending-approval messages.
  useEffect(() => {
    const gErr = params.get('google_error');
    const gPending = params.get('google_pending');
    const msg = params.get('msg');
    if (gErr) {
      toast.error(GOOGLE_ERROR_COPY[gErr] || `Google sign-in failed (${gErr})`);
      params.delete('google_error');
      setParams(params, { replace: true });
    } else if (gPending) {
      toast.success(msg ? decodeURIComponent(msg) : 'Account created — awaiting administrator approval.');
      params.delete('google_pending');
      params.delete('msg');
      setParams(params, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="login-page">
      {/* Left - Image Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-left"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 h-full">
          <div />
          <div className="space-y-6">
            <h1 className="font-serif text-4xl lg:text-5xl text-white leading-tight">
              Premium Growth<br />
              <span className="text-[#C5A059]">Command Center</span>
            </h1>
            <p className="text-white/80 text-lg max-w-md leading-relaxed">
              The Opportunity Intelligence Platform for reps who don't chase deals... They walk into them ready.
            </p>
            <div className="flex items-center gap-4 pt-4">
              <div className="w-12 h-px bg-[#C5A059]" />
              <span className="text-xs uppercase tracking-[0.2em] text-white/60">
                Executive Sales Platform
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Right - Form Panel */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md space-y-10">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <span className="text-2xl font-serif tracking-wide text-[#1A1A1A]">
              <span className="text-[#B89B5E] font-semibold">DH</span>
              <span className="font-light ml-1">Design</span>
            </span>
          </div>

          {/* Header */}
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Welcome back
            </p>
            <h2 className="font-serif text-3xl lg:text-4xl">Sign in to continue</h2>
            <p className="text-muted-foreground">
              Access your style opportunities and client relationships.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 rounded-sm border-border bg-transparent focus:border-primary focus:ring-0 transition-colors"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12 rounded-sm border-border bg-transparent focus:border-primary focus:ring-0 transition-colors"
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 rounded-sm uppercase tracking-widest text-xs font-medium bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Authenticating
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>

          {/* Google OAuth divider + button */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-border" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Or continue with
              </span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 rounded-sm uppercase tracking-widest text-xs font-medium border-border bg-transparent hover:bg-secondary/40 transition-colors flex items-center justify-center gap-3"
              onClick={() => {
                // Native Google OAuth — kicks off the OAuth Authorization
                // Code flow against DH Design's own GCP client. No
                // intermediary domain; user sees DH Design's consent
                // screen and returns to /auth/callback?exchange_id=...
                const API_URL = process.env.REACT_APP_BACKEND_URL;
                window.location.href = `${API_URL}/api/auth/google/start`;
              }}
              data-testid="login-google-btn"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#EA4335" d="M12 11.4v2.7h6.3c-.27 1.6-1.86 4.7-6.3 4.7-3.8 0-6.9-3.1-6.9-7s3.1-7 6.9-7c2.16 0 3.6.92 4.43 1.7l3.02-2.9C17.4 2 14.9 1 12 1 6.5 1 2 5.5 2 11s4.5 10 10 10c5.78 0 9.6-4.06 9.6-9.78 0-.66-.07-1.16-.16-1.62H12z" />
              </svg>
              Sign in with Google
            </Button>
          </div>

          {/* Footer */}
          <div className="pt-6 border-t border-border space-y-3">
            <p className="text-center text-sm text-muted-foreground">
              New to the platform?{' '}
              <Link
                to="/pricing"
                className="text-foreground hover:text-[#B89B5E] transition-colors underline underline-offset-4"
                data-testid="register-link"
              >
                Start free trial
              </Link>
            </p>
            <p className="text-center text-[11px] text-muted-foreground">
              Want admin-approved early access?{' '}
              <Link
                to="/register"
                className="hover:text-foreground transition-colors underline underline-offset-4"
                data-testid="waitlist-link"
              >
                Request access →
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
